// Copyright 2025, University of Maryland, All Rights Reserved
import { useState } from "react"
import { useMutation } from "@tanstack/react-query"
import { setApi } from "@/api/set"
import { useAuth } from "@umd-dit-ai-workspace/workspaces-component"
import { JobPoller, JobStatus } from "@umd-dit-ai-workspace/jobs-component"
import { AnalysisReport } from "@/components/AnalysisReport"
import { exportToAccessiblePDF } from "@/utils/accessiblePdfExport"
import type { SetProcessResponse } from "@/api/set"
import { FileText, Download, AlertCircle, X, Upload } from "lucide-react"

const SET_PROMPT_TYPE = "default"

const ANALYSIS_MODES = [
  {
    id: 'regular',
    label: 'Regular Analysis',
    description: 'Extract themes and insights from instructor evaluations',
    detail: 'Extract themes, corresponding quotes and overall sentiment from the evaluations. If more than one file is included you will receive full themes and quotes for each document, a cross-document summary, areas for improvement over time, trends and general recommendations.',
    promptName: 'SET prompt',
  },
  {
    id: 'longitudinal',
    label: 'Longitudinal Analysis',
    description: "Track an instructor's progression across multiple semesters",
    detail: 'Track changes in student comments over time (requires more than one file/time point). This report will provide a more concise individual evaluation summary, a sentiment summary by document, a longitudinal progression table, cross-document synthesis, actionable recommendations and a conclusion.',
    promptName: 'SET Longitudinal prompt',
  },
  {
    id: 'open_ended',
    label: 'Open Ended Questions',
    description: 'Analyse responses to open-ended survey questions',
    detail: 'Extract themes from open ended questions. This is useful if you ask open ended questions to your class and want a quick summary.',
    promptName: 'Open Ended Questions prompt',
  },
]

export default function SetAnalysisPage() {
  const { selectedOrganizationId, selectedWorkspaceId, userId } = useAuth()

  const [files, setFiles] = useState<File[]>([])
  const [selectedModeId, setSelectedModeId] = useState('regular')
  const [result, setResult] = useState<SetProcessResponse | null>(null)
  const [processingError, setProcessingError] = useState<string | null>(null)
  const [currentJobId, setCurrentJobId] = useState<string | null>(null)
  const [jobStatus, setJobStatus] = useState<string | null>(null)
  const [downloadingPdf, setDownloadingPdf] = useState(false)

  const activeMode = ANALYSIS_MODES.find(m => m.id === selectedModeId)!

  // Queue the job — returns {job_id, status}
  const mutation = useMutation({
    mutationFn: () => {
      if (!selectedOrganizationId || !selectedWorkspaceId || !userId) {
        throw new Error('Organization, workspace, and user are required')
      }
      if (files.length === 0) throw new Error('Please select at least one file')
      return setApi.processFiles(
        files,
        activeMode.promptName,
        SET_PROMPT_TYPE,
        selectedOrganizationId,
        selectedWorkspaceId,
        userId
      )
    },
    onSuccess: (data) => {
      setCurrentJobId(data.job_id)
      setJobStatus('PENDING')
      setProcessingError(null)
    },
    onError: (error: any) => {
      setProcessingError(error?.message || 'Processing failed. Please try again.')
    },
  })

  // Called by JobPoller when the job reaches COMPLETED status
  const handleJobComplete = async () => {
    if (!currentJobId || !selectedOrganizationId || !selectedWorkspaceId) return
    const jobId = currentJobId
    try {
      const data = await setApi.fetchResult(jobId, selectedOrganizationId, selectedWorkspaceId)
      setResult(data)
      setCurrentJobId(null)
      setJobStatus(null)
    } catch (err: any) {
      setProcessingError(err?.message || 'Failed to fetch result.')
      setCurrentJobId(null)
      setJobStatus(null)
    }
  }

  const handleJobError = (error: Error) => {
    setProcessingError(error?.message || 'Analysis job failed. Please try again.')
    setCurrentJobId(null)
    setJobStatus(null)
  }

  const handleDownload = async () => {
    if (!result) return
    setDownloadingPdf(true)
    try {
      const timestamp = new Date().toISOString().slice(0, 10)
      await exportToAccessiblePDF(result, {
        filename: `SET_Analysis_${timestamp}.pdf`,
        title: 'SET Analysis Report',
      })
    } catch (err) {
      console.error('PDF export failed:', err)
    } finally {
      setDownloadingPdf(false)
    }
  }

  const handleReset = () => {
    setFiles([])
    setResult(null)
    setProcessingError(null)
    setCurrentJobId(null)
    setJobStatus(null)
    mutation.reset()
  }

  const isProcessing = mutation.isPending || Boolean(currentJobId)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-8 rounded-xl shadow-lg">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Feedback Analysis</h1>
        <p className="text-gray-600">
          Upload one or more instructor evaluation files to extract themes and insights.
          Results are not saved to the system.
        </p>
        <p className="text-gray-600 mt-4">
          Once you have uploaded one or more files you will be able to do one of 3 analyses:
        </p>
        <ol className="list-decimal list-outside ml-5 mt-2 space-y-2 text-sm text-gray-600">
          {ANALYSIS_MODES.map(mode => (
            <li key={mode.id}>
              <span className="font-medium text-gray-800">{mode.label}</span> &mdash; {mode.detail}
            </li>
          ))}
        </ol>
      </div>

      {/* File Upload */}
      <div className="bg-white p-8 rounded-xl shadow-lg">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Upload Files</h2>

        <label htmlFor="set-file-input" className="block text-sm font-medium text-gray-700 mb-2">
          Select Files <span className="text-gray-400">(PDF, DOCX, TXT, PPTX)</span>
        </label>
        {/* The native input is visually hidden and driven by the label below: its
            value is cleared on every change (so re-picking the same file still
            fires onChange), which would otherwise reset its built-in text to
            "No file chosen" even though the files are held in state. */}
        <div className="flex items-center gap-3">
          <input
            id="set-file-input"
            type="file"
            multiple
            accept=".pdf,.docx,.txt,.pptx"
            disabled={isProcessing}
            onChange={e => {
              const selected = Array.from(e.target.files || [])
              setFiles(prev => {
                const existing = new Set(prev.map(f => f.name + f.size))
                return [...prev, ...selected.filter(f => !existing.has(f.name + f.size))]
              })
              e.target.value = ''
            }}
            className="sr-only peer"
          />
          <label
            htmlFor="set-file-input"
            className={`inline-flex items-center gap-2 py-2 px-4 rounded text-sm font-semibold bg-blue-50 text-blue-700 peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500 peer-focus-visible:ring-offset-2 ${
              isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-blue-100'
            }`}
          >
            <Upload className="h-4 w-4" />
            Choose Files
          </label>
          <span className="text-sm text-gray-500">
            {files.length === 0
              ? 'No files chosen'
              : `${files.length} file${files.length === 1 ? '' : 's'} chosen`}
          </span>
        </div>

        {files.length > 0 && (
          <ul className="mt-4 space-y-2">
            {files.map((f, i) => (
              <li
                key={i}
                className="flex items-center justify-between bg-gray-50 px-4 py-2 rounded-lg border border-gray-200"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-700 truncate">{f.name}</span>
                  <span className="text-xs text-gray-400 flex-shrink-0">
                    ({(f.size / 1024).toFixed(1)} KB)
                  </span>
                </div>
                <button
                  onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}
                  disabled={isProcessing}
                  className="ml-4 text-gray-400 hover:text-red-500 flex-shrink-0 disabled:opacity-40"
                  aria-label={`Remove ${f.name}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Analysis Options */}
      {files.length > 0 && (
        <div className="bg-white p-8 rounded-xl shadow-lg">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Analysis Options</h2>

          {/* Analysis type radio buttons */}
          <div className="space-y-2">
            {ANALYSIS_MODES.map(mode => (
              <label
                key={mode.id}
                className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                  selectedModeId === mode.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <input
                  type="radio"
                  name="analysisMode"
                  value={mode.id}
                  checked={selectedModeId === mode.id}
                  onChange={() => setSelectedModeId(mode.id)}
                  disabled={isProcessing}
                  className="mt-0.5 accent-blue-600"
                />
                <div>
                  <p className="text-sm font-medium text-gray-800">{mode.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{mode.description}</p>
                </div>
              </label>
            ))}
          </div>

          {processingError && (
            <div className="mt-4 flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              {processingError}
            </div>
          )}

          <button
            onClick={() => { setResult(null); setProcessingError(null); mutation.mutate() }}
            disabled={isProcessing || files.length === 0}
            className="mt-6 flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {isProcessing ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                Processing...
              </>
            ) : (
              'Process Files'
            )}
          </button>
        </div>
      )}

      {/* Job Poller */}
      {currentJobId && (
        <JobPoller
          jobId={currentJobId}
          jobContext={{
            org_id: selectedOrganizationId || undefined,
            ws_id: selectedWorkspaceId || undefined,
          }}
          onStatusChange={setJobStatus}
          onComplete={handleJobComplete}
          onError={handleJobError}
          timeout={10 * 60 * 1000}
        />
      )}

      {/* Job Status */}
      {currentJobId && (
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <JobStatus
            jobId={currentJobId}
            jobStatus={jobStatus}
            isProcessing={Boolean(currentJobId)}
            processDescription={`Analyzing ${files.length} file${files.length !== 1 ? 's' : ''}...`}
          />
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Analysis Result</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {result.file_count} file{result.file_count > 1 ? 's' : ''} &middot;{' '}
                {result.content_characters.toLocaleString()} characters processed
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleReset}
                className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                New Analysis
              </button>
              <button
                onClick={handleDownload}
                disabled={downloadingPdf}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              >
                {downloadingPdf ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    Preparing PDF...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Download PDF
                  </>
                )}
              </button>
            </div>
          </div>
          <AnalysisReport analysisData={result} />
        </div>
      )}
    </div>
  )
}
